import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, QueryList } from '@angular/core';
import { LinkupService } from '../link-up/linkup.service';
import { CopyService } from '../copies/copy.service';
import { TokenService } from '../security/token.service';
import { ESortDirection, findInArray, getValueProperty, memoize, sortArrayObject } from '../../helpers/functions.helpers';
import { ApplicationService } from '../application/application.service';
import { lastValueFrom } from '../../helpers/functions.helpers';

export enum EStateCatalogManager {
  ACTIVE = "Activa",
  INACTIVE = "Inactiva",
  ALL = ""
}

/**
 * Enumerador para los tipos de persona usados en el sistema
 */
export enum EPersonType {
  PERSON_TYPE_NAT = 'PN',
  PERSON_TYPE_JUR = 'PJ'
}

export enum EStatusBusinessList {
  ACTIVE = 'activo',
  INACTIVE = 'inactivo'
}

export interface IBusinessList {
  business_collection: string
  business_id: string
  business_name: string
  parent_business_id: string
  parent_business_collection: string
  business_channel_id: string
  business_application: string
  business_status: string
  parameters: string
}

@Injectable({
  providedIn: 'root'
})
export class CatalogService {

  private _cities: any;

  private _documentTypes: any;

  private _states: any;

  private _celulas: Array<any> = [];

  private _types: Array<any> = [];

  private _subtypes: Array<any> = [];

  private _segments: any;

  private _subsegments: any;

  private _economicActivities: any;

  private _customerTypes: any;

  private _genderTypes: any;

  private _gender: QueryList<any> | any;
  private _genderEquivalence: any = [{ id: 'F', description: 'Femenino', code: 'F' }, { id: 'M', description: 'Masculino', code: 'M' }, { id: 'O', description: 'Otros', code: 'O' }];
  private _maxRetry = 1;

  constructor(
    private _http: HttpClient,
    private _settings: LinkupService,
    private _copies: CopyService,
    private _token: TokenService,
    private app: ApplicationService
  ) {

  }

  get genders(): Promise<any> {
    return this.gender;
  }

  /**
 * Permite obtener el listado de ciudades
 */
  get cities(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (!this._cities || this._cities.length == 0) {
        let cities = await this.getResourceCatalogSolicitations('cities') as Array<any>;
        // Ajuste temporal mientras backend ajusta el registro para BOGOTA D.C.
        if (cities && Array.isArray(cities)) {
          cities = sortArrayObject(cities, "business_name", ESortDirection.ASC);
          this.cities = cities.map((item) => {
            return {
              id: item.business_id,
              name: `${item.business_name}`,
              stateId: item.parent_business_id
            };
          });
        }
      }
      resolve(this._cities);
    });
  }

  /**
   * Permite establecer el listado de ciudades
   */
  public set cities(value: any) {
    this._cities = value;
  }

  /**
   * Permite obtener el listado de ciudades por departamento
   * @param stateId
   * @returns
   */
  public async getCitiesByState(stateId: any) {
    const cities = await this.cities as Array<any>;
    if (cities && Array.isArray(cities)) {
      return cities.filter(value => value.stateId == stateId);
    } else {
      return [];
    }
  }

  /**
   * Permite obtener el listado de tipos de documento
   */
  get documentTypes(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      let documentTypes: Array<any> = await this.getResourceCatalogSolicitations('documents_types');
      this.documentTypes = documentTypes.map((item) => {
        return {
          id: item.business_id,
          name: `${item.business_name}`,
          parameters: item.parameters
        };
      });
      resolve(this._documentTypes);
    });
  }

  /**
   * Permite establecer el listado de tipos de documento
   */
  set documentTypes(value: any) {
    this._documentTypes = value;
  }

  /**
   * Permite obtener el listado de departamentos
   */
  get states(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (!this._states || this._states.length == 0) {
        let result = await this.getResourceCatalogSolicitations('departments');
        result = sortArrayObject(result, "business_name", ESortDirection.ASC);
        this.states = result.map((item) => {
          return {
            id: item.business_id,
            name: `${item.business_name}`
          };
        });
      }
      resolve(this._states);
    });
  }

  /**
   * Permite establecer el listado de departamentos
   */
  set states(value: any) {
    this._states = value;
  }

  /**
  * Permite obtener el listado de actividades economicas
  */
  get economicActivities(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (!this._economicActivities || this._economicActivities.length == 0) {
        let result = await this.getResourceCatalogSolicitations('code_ciiu');
        this.economicActivities = result.map((item) => {
          return {
            id: item.business_id,
            name: `${item.business_id} - ${item.business_name}`
          };
        });
      }
      resolve(this._economicActivities);
    });
  }

  /**
   * Permite establecer el listado de actividades economicas
   */
  set economicActivities(value: any) {
    this._economicActivities = value;
  }

  /**
   * Permite obtener el listado de celulas
   */
  get celulas(): Promise<Array<{ id: string, name: string }>> {

    return new Promise(async (resolve, reject) => {
      if (!this._celulas || this._celulas.length == 0) {
        const celluleData = await this.getResourceCatalogSolicitations('solicitation_cellule');
        this.celulas = celluleData.map((item) => {
          return {
            'id': item.business_id,
            'name': item.business_name,
          };
        });
      }
      resolve(this._celulas);
    });
  }

  /**
   * Permite establecer el listado de celulas
   */
  public set celulas(value: any) {
    this._celulas = value;
  }

  /**
   * Permite obtener una celula por su identificador
   * @param id Identificador de la celula
   * @returns Listado de celulas
   */
  async getCelulaById(id: any) {
    const celulas = await this.celulas;
    return findInArray(celulas, 'id', id);
  }

  /**
   * Permite obtener los tipos de celula
   * @param celulaId Identificador de la celula para la cual se deben traer los tipo
   */
  async getCelulaTypes(celulaId: any = null) {

    let result: Array<any> = [];

    if (!this._types || this._types.length == 0) {
      let params: any = {};
      const subtypeData = await this.getResourceCatalogSolicitations('solicitation_type', params);
      result = subtypeData.map((item) => {
        return {
          'id': item.business_id,
          'name': item.business_name,
          celulaId: item.parent_business_id,
          parameters: item.parameters
        };
      });
      this._types = result;
    }

    if (this._types && celulaId) {
      result = this._types.filter((item) => { return item.celulaId == celulaId });
    } else if (this._types) {
      result = this._types;
    }
    return result;
  }

  /**
   * Permite obtener un tipo de celula por identificador
   * @param celulaId Identificador de la celula
   * @param id Identificador del tipo de celula
   */
  async getCelulaTypeById(id: any, celulaId: any = null) {
    const types = await this.getCelulaTypes(celulaId);
    return findInArray(types, 'id', id);
  }

  /**
   * Permite obtener los subtipos de celula
   * @param celulaId Identificador de la celula
   * @param typeId Identificador del tipo de celula
   * @returns Listado de subtipos
   */
  async getCelulaTypeSubtypes(celulaId: any = null, typeId: any = null) {
    let result: Array<any> = [];

    if (!this._subtypes || this._subtypes.length == 0) {

      let params: any = {};
      /**if (typeId) {
        params["parent_business_id"] = typeId;
      }*/
      const subtypeData = await this.getResourceCatalogSolicitations('solicitation_subtype', params);

      result = subtypeData.map((item) => {
        return {
          id: item.business_id,
          name: item.business_name,
          typeId: item.parent_business_id,
          celulaId,
          parameters: item.parameters
        };
      });

      this._subtypes = result;

    }

    if (this._subtypes && typeId) {
      result = this._subtypes.filter((item) => { return item.typeId == typeId });
    } else if (this._subtypes) {
      result = this._subtypes;
    }

    return result;

  }

  /**
   * Permite obtener un subtipo de celula por identificador
   * @param id Identificador del subtipo de la celula
   * @param typeId Identificador del tipo de celula
   * @param celulaId Identificador de la celula
   * @returns Subtipo de la celula
   */
  async getCelulaTypeSubtypeById(id: any, typeId: any = null, celulaId: any = null) {
    const subtypes = await this.getCelulaTypeSubtypes(celulaId, typeId);
    return findInArray(subtypes, 'id', id);
  }

  /**
   * Permite construir de manera dinamica los encabezados para las peticiones al servicio
   */
  async httpOptions() {
    const { accessToken } = await this._token.accessToken;
    const transaction_id = await this._token.transactionId;
    return {
      headers: {
        "Authorization": `Bearer ${accessToken}`,
        "application": this.app.channelName, /** @todo Utilizar channel comunicator*/
        "channel_id": this.app.channelId,
        "timestamp": new Date().toISOString(),
        "transaction_id": transaction_id,
        "Content_Type": "application/json"
      }
    };
  }

  /**
   * Permite construir de manera dinamica los encabezados para las peticiones al servicio
   */
  async httpOptionsSolicitations() {
    const { accessToken } = await this._token.accessToken;
    const transaction_id = await this._token.transactionId;
    const session_id = await this._token.sessionId;

    return {
      headers: {
        "Authorization": `Bearer ${accessToken}`,
        "application": this.app.application,
        "channel-id": this.app.channelId,
        "terminal_id": "127.0.0.1",
        "timestamp": new Date().toISOString(),
        "transaction_id": transaction_id,
        "session_id": session_id,
        "token-client-id": "0000000_1"
      }
    };
  }

  /**
   * Permite cargar los datos de un recurso desde el catalogo
   * @param name
   * @param state
   * @returns
   */
  getResourceCatalog(name: string, state: EStateCatalogManager = EStateCatalogManager.ACTIVE, retry: number = 0): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const contextUrls = await this._settings.contextUrls;
        const service = `${name}/${state}`;
        const URL = `${contextUrls.URLS.CATALOG_MANAGER_V1}${service}`;
        const httpOptions = await this.httpOptions();
        const req$ = this._http.get(URL, httpOptions);
        const response: any = await lastValueFrom(req$);

        if (response && response.hasOwnProperty(name)) {
          resolve(response[name]);
        } else {
          resolve(response);
        }
      } catch (error) {
        const status = getValueProperty(error, 'status', 500);
        if (status === 403 && retry < this.maxRetry) {
          resolve(await this.getResourceCatalog(name, state, ++retry))
        } else {
          console.info(`No es posible obtener el catalogo ${name}`, error);
          resolve([]);
        }

      }
    });
  }

  /**
 * Permite obtener el listado de segmentos
 */
  get segments(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (!this._segments || this._segments.length == 0) {
        const segmentData = await this.getResourceCatalogSolicitations('segment');
        this.segments = segmentData.map((item) => {
          return {
            'id': item.business_id,
            'name': item.business_name,
          };
        });
      }

      resolve(this._segments);
    });
  }

  /**
   * Permite establecer el listado de segmentos
   */
  public set segments(value: any) {
    this._segments = value;
  }

  /**
   * Permite obtener un segmento por su identificador
   * @param id Identificador del segmento
   * @returns Segmento
   */
  async getSegmentById(id: any) {
    const segments = await this.segments;
    return findInArray(segments, 'id', id);
  }

  /**
   * Permite obtener los subsegmentos del segmento
   * @param segmentId Identificador del segmento para la cual se deben traer los subsegmentos
   */
  async getSubsegments(segmentId: any = null) {
    let result: Array<any> = [];
    if (!this._subsegments || this._subsegments.length == 0) {
      let params: any = {};
      const subsegmentData = await this.getResourceCatalogSolicitations('subsegment', params);
      result = subsegmentData.map((item) => {
        return {
          'id': item.business_id,
          'name': item.business_name,
          segmentId: item.parent_business_id
        };
      });
      this._subsegments = result;
    }

    if (this._subsegments && segmentId) {
      result = this._subsegments.filter((item: any) => { return item.segmentId == segmentId });
    } else if (this._subsegments) {
      result = this._subsegments;
    }
    return result;
  }

  /**
   * Permite obtener un subsegmento por identificador
   * @param segmentId Identificador del segmento
   * @param id Identificador del subsegmento
   */
  async getSubsegmentById(id: any, segmentId: any = null) {
    const subsegments = await this.getSubsegments(segmentId);
    return findInArray(subsegments, 'id', id);
  }

  /**
   * Permite obtener el listado de tipos de naturaleza del cliente
   */
  get customerTypes(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (!this._customerTypes || this._customerTypes.length == 0) {
        this.customerTypes = [
          {
            id: EPersonType.PERSON_TYPE_JUR,
            desc: "Persona Jurídica",
            onbase: 'PERSONA JURIDICA'
          },
          {
            id: EPersonType.PERSON_TYPE_NAT,
            desc: "Persona Natural",
            onbase: 'PERSONA NATURAL'
          }
        ];
      }
      resolve(this._customerTypes);
    });
  }

  /**
   * Permite establecer el listado de tipos de naturaleza del cliente
   */
  set customerTypes(value: any) {
    this._customerTypes = value;
  }

  /**
  * Permite obtener el listado de generos
  */
  get gender(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (!this._gender || this._gender.length == 0) {
        let result = await this.getResourceCatalogSolicitations('genders');
        result = sortArrayObject(result, "business_name", ESortDirection.ASC);

        this.gender = result.map((item) => {
          let phoenix = findInArray(getValueProperty(item, 'parameters', []), "key", "phoenix");
          return {
            id: item.business_id,
            name: `${item.business_name}`,
            phoenix: getValueProperty(phoenix, "value")
          };
        });
      }
      resolve(this._gender);
    });
  }

  /**
 * Permite establecer el listado de generos
 */
  set gender(value: any) {
    this._gender = value;
  }

  /**
   * Permite cargar los datos de un recurso desde el catalogo
   * @param name
   * @param state
   * @returns
   */
  getResourceCatalogSolicitations(business_collection: string, params?: any, retry: number = 0): Promise<Array<IBusinessList>> {

    return new Promise(async (resolve, reject) => {
      try {
        const settings = await this._settings.settings;
        const businessCollections = getValueProperty(settings, 'request.CATALOG_SOLICITATIONS.BUSINESS_COLLECTIONS', []);
        const businessCollection = findInArray(businessCollections, 'idFrontend', business_collection)
        const contextUrls = await this._settings.contextUrls;
        const URL = `${contextUrls.URLS.CATALOG_SOLICITATIONS}`;
        const httpOptions = await this.httpOptionsSolicitations();
        let body: { data: IBusinessList } = {
          data: {
            business_collection: getValueProperty(businessCollection, 'idBackend', business_collection),
            business_application: this.app.currentApplication,
            business_channel_id: this.app.channelId,
            business_status: EStatusBusinessList.ACTIVE,
            ...(params || {})
          }
        };
        const req$ = this._http.post(URL, body, httpOptions);
        const response: any = await lastValueFrom(req$);
        if (response && response.hasOwnProperty('data')) {
          let resultData = response['data'];
          sortArrayObject(resultData, 'business_name', ESortDirection.ASC);
          resolve(resultData);
        } else if (response && response.hasOwnProperty('errorMessage')) {
          throw new Error(response.errorMessage);
        } else {
          resolve(response);
        }
      } catch (error) {
        const status = getValueProperty(error, 'status', 500);
        if (status === 403 && retry < this.maxRetry) {
          resolve(await this.getResourceCatalogSolicitations(business_collection, params, ++retry))
        } else {
          console.info(`No es posible obtener el catalogo ${business_collection}`);
          resolve([]);
        }
      }
    });
  }

  /**
   * Permite vaciar el cache de aplicacion para los catalogos existentes
   */
  clearCache() {
    this._celulas = [];
    this._types = [];
    this._subtypes = [];
    this._states = [];
    this._cities = [];
    this._documentTypes = [];
    this._gender = [];
    this._segments = [];
    this._subsegments = [];
    this._economicActivities = [];
  }

  get maxRetry() {
    return this._maxRetry;
  }

  set maxRetry(value: number) {
    this._maxRetry = value;
  }

}
